import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { listSmsApprovals, setSmsApproval } from "../../api/smsGateway";
import { canInvoiceTopup, decideTopup, listSellerWallets, listWalletItems, putWalletItem, raiseTopupInvoice, rupeesToPaise } from "../../api/wallet";
import { formatInrPaise } from "../../domain/tiers";
import { flagEnabled } from "../../config";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * io.ankhos's side of the SMS fund: the price, every operator's balance, and the
 * payments to confirm. radaccess raises the invoice when one is confirmed;
 * Invoice repeats it safely if that call failed.
 */
export function SmsFundsPlatformScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const qc = useQueryClient();
  const enabled = Boolean(identity.token && ns);
  const incoming = useQuery({ queryKey: ["smsfunds", ns], enabled, queryFn: ({ signal }) => listSellerWallets(identity, ns, signal) });
  const items = useQuery({ queryKey: ["smsfundprice", ns], enabled, queryFn: ({ signal }) => listWalletItems(identity, ns, signal) });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["smsfunds", ns] });
  const [price, setPrice] = useState("");
  const current = (items.data ?? []).find((i) => i.itemkey === "sms" && i.active);
  const parsed = rupeesToPaise(price, 0.01, 1_000_000);
  const savePrice = useMutation({
    mutationFn: () => putWalletItem(identity, ns, { itemkey: "sms", name: "SMS", billing: "one_time", pricepaise: parsed as number, gstratebp: 1800, priceincludesgst: true }),
    onSuccess: () => {
      setPrice("");
      void qc.invalidateQueries({ queryKey: ["smsfundprice", ns] });
    },
  });
  const decide = useMutation({ mutationFn: (v: { id: number; confirm: boolean; reason: string }) => decideTopup(identity, ns, v.id, v.confirm, v.reason), onSettled: refresh });
  const invoice = useMutation({ mutationFn: (id: number) => raiseTopupInvoice(identity, ns, id), onSettled: refresh });
  const entries = (incoming.data?.entries ?? []).filter((e) => e.kind === "topup" && e.itemkey === "sms");
  const own = flagEnabled("sms.gateway.own");
  const approvals = useQuery({ queryKey: ["smsapprovals", ns], enabled: enabled && own, queryFn: ({ signal }) => listSmsApprovals(identity, ns, signal) });
  const approve = useMutation({
    mutationFn: (v: { id: string; approved: boolean }) => setSmsApproval(identity, ns, v.id, v.approved),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["smsapprovals", ns] }),
  });
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const failure = incoming.error ?? decide.error ?? invoice.error ?? savePrice.error ?? approvals.error ?? approve.error;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h5" component="h1">SMS funds</Typography>
        <Typography variant="body2" color="text.secondary">Operators' prepaid balances for SMS through the AnkhOS gateway.</Typography>
      </Box>
      {failure && <Alert severity="error">{errText(failure)}</Alert>}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Price per SMS, including GST</Typography>
        <Typography variant="body2" gutterBottom>
          {current ? `Now ${formatInrPaise((current.pricepaise) / 100)}${current.priceincludesgst ? " including GST" : " + GST"}. A change applies from the next minute; past deductions stay as they were.` : "No price is set, so nothing is deducted and no operator is stopped. Setting a price starts charging, and is not retroactive."}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField size="small" label="Rupees, e.g. 0.35" value={price} onChange={(e) => setPrice(e.target.value)} error={price !== "" && typeof parsed === "string"} helperText={price !== "" && typeof parsed === "string" ? parsed : " "} />
          <Button variant="outlined" disabled={typeof parsed === "string" || savePrice.isPending} onClick={() => savePrice.mutate()}>Set price</Button>
        </Stack>
      </Paper>
      {own && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Operators' own SMS accounts</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            An operator's own account sends nothing until you approve it. Read its test messages first. A new key or sender needs a new approval.
          </Typography>
          <TableContainer>
            <Table size="small" aria-label="Operators' own SMS accounts">
              <TableHead>
                <TableRow>
                  <TableCell>Operator</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Sender</TableCell>
                  <TableCell>Test message</TableCell>
                  <TableCell align="right">Approval</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(approvals.data ?? []).length === 0 && <TableRow><TableCell colSpan={5}>No accounts yet.</TableCell></TableRow>}
                {(approvals.data ?? []).map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{a.namespace}</TableCell>
                    <TableCell>{a.label}</TableCell>
                    <TableCell>{a.sender_id}</TableCell>
                    <TableCell>{a.status === "Verified" ? "Sent" : a.status}</TableCell>
                    <TableCell align="right">
                      {a.approved_at
                        ? <Button size="small" color="error" disabled={approve.isPending} onClick={() => approve.mutate({ id: a.id, approved: false })}>Withdraw</Button>
                        : <Button size="small" variant="contained" disabled={a.status !== "Verified" || approve.isPending} onClick={() => approve.mutate({ id: a.id, approved: true })}>Approve</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Operators' SMS funds">
          <TableHead><TableRow><TableCell>Operator</TableCell><TableCell align="right">Balance</TableCell><TableCell align="right">Waiting</TableCell></TableRow></TableHead>
          <TableBody>
            {(incoming.data?.wallets ?? []).length === 0 && <TableRow><TableCell colSpan={3}>No operator has a fund yet.</TableCell></TableRow>}
            {(incoming.data?.wallets ?? []).map((w) => (
              <TableRow key={w.holdernamespace}>
                <TableCell>{w.holdernamespace}</TableCell>
                <TableCell align="right">{formatInrPaise((w.balancepaise) / 100)}</TableCell>
                <TableCell align="right">{formatInrPaise((w.pendingpaise) / 100)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="SMS fund payments">
          <TableHead><TableRow><TableCell>Operator</TableCell><TableCell>Reference</TableCell><TableCell align="right">Amount</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
          <TableBody>
            {entries.length === 0 && <TableRow><TableCell colSpan={5}>No payments yet.</TableCell></TableRow>}
            {entries.map((e) => (
              <TableRow key={e.id}>
                <TableCell>{e.holdernamespace}</TableCell>
                <TableCell>{e.paymentreference}</TableCell>
                <TableCell align="right">{formatInrPaise((e.amountpaise) / 100)}</TableCell>
                <TableCell>{e.status}{e.decisionreason ? `: ${e.decisionreason}` : ""}</TableCell>
                <TableCell align="right">
                  {e.status === "pending" && (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="contained" disabled={decide.isPending} onClick={() => decide.mutate({ id: e.id, confirm: true, reason: "" })}>Confirm</Button>
                      <Button size="small" disabled={decide.isPending} onClick={() => { setRejecting(e.id); setReason(""); }}>Reject</Button>
                    </Stack>
                  )}
                  {canInvoiceTopup(e) && <Button size="small" disabled={invoice.isPending} onClick={() => invoice.mutate(e.id)}>Invoice</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={rejecting !== null} onClose={() => setRejecting(null)} fullWidth maxWidth="xs">
        <DialogTitle>Reject this payment</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth sx={{ mt: 1 }} label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)}>Cancel</Button>
          <Button color="error" disabled={reason.trim() === ""} onClick={() => { decide.mutate({ id: rejecting as number, confirm: false, reason }); setRejecting(null); }}>Reject</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
